/**
 * Phieu lo de dang LUOI: moi dong mot so (hoac mot bo so) voi MUC DIEM RIENG.
 *
 * Khach phan hoi 08/10/2026: go nhieu so vao mot o roi dung chung mot muc diem thi de
 * lon - khong nhin ra so nao dat bao nhieu, trung thi duoc bao nhieu. Luoi ba cot
 * "So | Diem | Tien trung" cho moi so mot hang, doi chieu bang mat duoc.
 *
 * Phan tinh toan tach khoi man hinh de kiem bang don vi: day la cho tinh TIEN, sai
 * mot phep nhan la nguoi choi thay mot con so khac con so bi tru.
 */

import type { LodeLineInput } from './lode-api';
import { LODE_BET_TYPES, checkPicks } from './lode-bet-types';

/** Thoi gian giu phieu cho xac nhan. Qua han thi phai bam dat lai (ty le, gio chot co the da doi). */
export const CONFIRM_WINDOW_MS = 2 * 60 * 1000;

export interface SlipRow {
  /** Khoa React - khong gui len may chu. */
  key: string;
  numbersRaw: string;
  points: string;
}

let rowSeq = 0;
export function newRow(points = '1', numbersRaw = ''): SlipRow {
  rowSeq += 1;
  return { key: `r${rowSeq}`, numbersRaw, points };
}

/**
 * Tach chuoi nguoi choi go thanh danh sach so. Nhan moi ky tu khong phai chu so lam dau
 * ngan (phay, cham, khoang trang). Mot cum go lien dai dung boi so cua `digits` thi cat
 * thanh tung so: ban phim so cua dien thoai khong co phim cach, nen "115180" la 11, 51, 80.
 */
export function parseNumbers(raw: string, digits: number): number[] {
  return raw
    .split(/[^0-9]+/)
    .filter((s) => s.length > 0)
    .flatMap((s) => (s.length > digits && s.length % digits === 0
      ? Array.from({ length: s.length / digits }, (_, i) => s.slice(i * digits, (i + 1) * digits))
      : [s]))
    .map((s) => Number(s));
}

/** So diem nguoi choi go -> nua diem gui may chu; `null` khi chua hop le. */
export function pointsX2Of(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+([.,]\d+)?$/.test(s)) return null;
  const x2 = Number(s.replace(',', '.')) * 2;
  return Number.isInteger(x2) && x2 >= 2 ? x2 : null;
}

/**
 * Loai cuoc chi mot so: go nhieu so vao mot o thi tach ra moi so mot hang, cung muc
 * diem. Giu cach go nhanh "115180" van dung duoc tren luoi.
 */
export function expandRows(rows: readonly SlipRow[], betType: string): SlipRow[] {
  const meta = LODE_BET_TYPES[betType];
  if (!meta || meta.picks !== 1) return [...rows];
  return rows.flatMap((row) => {
    const nums = parseNumbers(row.numbersRaw, meta.digits);
    if (nums.length <= 1) return [row];
    return nums.map((n, i) => {
      const raw = String(n).padStart(meta.digits, '0');
      return i === 0 ? { ...row, numbersRaw: raw } : newRow(row.points, raw);
    });
  });
}

/** So dong nguyen tu MOT tap so sinh ra tren MOT dai (bao lo tinh theo dai o noi goi). */
function atomicPerDraw(betType: string, numberCount: number): number {
  if (betType === 'LO_KEP') return 10;
  if (betType === 'DAU_DUOI') return 2;
  if (betType === 'DAN_DE') return numberCount;
  return 1;
}

export interface RowCalc {
  key: string;
  /** Thu tu hien thi, dem tu 1. */
  index: number;
  numbers: number[];
  pointsX2: number | null;
  /** So dong tinh tien cua hang nay tren moi dai da chon. */
  atomicLines: number;
  stakeMinor: bigint | null;
  /** Trung thi nhan, moi dong (moi nhay voi lo). Khi cac dai lech ty le: muc THAP nhat. */
  payoutMinor: bigint | null;
  /** Muc CAO nhat trong cac dai da chon; bang `payoutMinor` khi moi dai cung ty le. */
  payoutMaxMinor: bigint | null;
  error: string | null;
}

export interface SlipCalc {
  rows: RowCalc[];
  lines: LodeLineInput[];
  atomicLines: number;
  stakeMinor: bigint;
  /** Loi dau tien can bao, `null` khi gui duoc. */
  error: string | null;
}

export interface SlipOdds {
  pointPriceMinor: bigint;
  payoutPerPointMinor: bigint;
}

/**
 * Tinh toan bo phieu: tung hang (so, diem, tien cuoc, tien trung) va cac dong gui may chu.
 *
 * Hang bo trong o So thi bo qua (tru lo kep - loai do khong nhap so), de nguoi choi
 * them san vai hang trong ma khong bi bao loi.
 */
export function calcSlip(
  betType: string,
  rows: readonly SlipRow[],
  drawIds: readonly string[],
  odds: SlipOdds | null,
  /**
   * Ty le rieng cua tung ky, khi cac dai da chon khong cung mot muc tra.
   * Thieu ky nao thi ky do dung `odds` (muc mien).
   */
  oddsByDraw?: ReadonlyMap<string, SlipOdds>,
): SlipCalc {
  const meta = LODE_BET_TYPES[betType];
  const drawCount = drawIds.length;
  const calcs: RowCalc[] = [];
  const lines: LodeLineInput[] = [];
  let atomicTotal = 0;
  let stakeTotal = 0n;
  let firstError: string | null = null;

  const used = rows.filter((r) => betType === 'LO_KEP' || r.numbersRaw.trim() !== '');
  const seen = new Map<number, number>();

  used.forEach((row, i) => {
    const index = i + 1;
    const numbers = betType === 'LO_KEP' ? [] : parseNumbers(row.numbersRaw, meta?.digits ?? 2);
    const pointsX2 = pointsX2Of(row.points);
    let error: string | null = null;

    if (!meta) error = 'Chọn loại cược.';
    else if (meta.picks === 1 && numbers.length > 1) error = 'Mỗi hàng một số; số khác thêm hàng mới.';
    else error = checkPicks(betType, numbers);

    if (!error && meta?.picks === 1) {
      const n = numbers[0]!;
      const before = seen.get(n);
      if (before !== undefined) {
        error = `Số ${String(n).padStart(meta.digits, '0')} trùng hàng ${before}; sửa điểm ở hàng đó.`;
      } else {
        seen.set(n, index);
      }
    }
    if (!error && pointsX2 === null) error = 'Điểm phải từ 1, nhận nửa điểm (ví dụ 1,5).';

    const perDraw = atomicPerDraw(betType, numbers.length);
    const atomicLines = perDraw * Math.max(drawCount, 1);

    // Ty le cua tung ky da chon. May chu lam tron SAN tung dong nguyen tu roi moi cong,
    // nen o day cung cong tung dai thay vi nhan mot gia voi so dai - nhan truoc se lech
    // vai dong so voi so tien that su bi tru.
    const perDrawOdds = drawIds.map((id) => oddsByDraw?.get(id) ?? odds);
    const usable = perDrawOdds.filter((o): o is SlipOdds => o !== null);
    const allDrawsPriced = drawCount === 0 || usable.length === drawCount;

    let stakeMinor: bigint | null = null;
    let payoutMinor: bigint | null = null;
    let payoutMaxMinor: bigint | null = null;
    if (pointsX2 !== null && odds && allDrawsPriced) {
      const list = drawCount === 0 ? [odds] : usable;
      stakeMinor = drawCount === 0
        ? 0n
        : list.reduce(
          (sum, o) => sum + (BigInt(pointsX2) * o.pointPriceMinor) / 2n * BigInt(perDraw),
          0n,
        );
      const payouts = list.map((o) => (BigInt(pointsX2) * o.payoutPerPointMinor) / 2n);
      payoutMinor = payouts.reduce((a, b) => (b < a ? b : a));
      payoutMaxMinor = payouts.reduce((a, b) => (b > a ? b : a));
    }

    calcs.push({
      key: row.key, index, numbers, pointsX2, atomicLines, stakeMinor, payoutMinor, payoutMaxMinor, error,
    });
    if (error) {
      firstError ??= `Hàng ${index}: ${error}`;
      return;
    }

    atomicTotal += perDraw * drawCount;
    stakeTotal += stakeMinor ?? 0n;
    if (meta!.multiDraw) {
      lines.push({ draw_ids: [...drawIds], bet_type: betType, numbers, points_x2: pointsX2! });
    } else {
      for (const drawId of drawIds) {
        lines.push({ draw_id: drawId, bet_type: betType, numbers, points_x2: pointsX2! });
      }
    }
  });

  if (!firstError && used.length === 0) {
    firstError = 'Chưa nhập số. Gõ số vào cột «Số», ví dụ 12.';
  }

  return { rows: calcs, lines, atomicLines: atomicTotal, stakeMinor: stakeTotal, error: firstError };
}
